import React, { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { config } from '../../../config'
import { context, SeoExtra } from '../../../context'

type SeoProps = {
    pageName: string
}

const Seo: React.FunctionComponent<SeoProps> = ({ pageName }) => {
    const { t, i18n } = useTranslation('seo')
    const location  = useLocation()
    const routeParams = useParams()

    const extra = useExtraSeo(pageName, location, routeParams)

    const { host } = config.setup
    
    // Canonical url
    const canonical = addHost(host, `${location.pathname}${location.search}`)

    // Theme for mobile chrome top bar
    const theme = useTheme()
    // @ts-ignore
    const accentColor = theme.colors.accent

    const tr = (key: string) => t(`${pageName}.${key}`)

    // We have different thumbnail for languages
    let thumbnail = require(`./images/thumbnail_${i18n.language}.png`)
    if (['awards_2021', 'awards_2022', 'awards_2023'].includes(pageName)) {
        thumbnail = require(`./images/thumbnail_${pageName}.png`)
    }

    const title = extra?.title || tr('page_title') || 'Byzans' 
    const description = extra?.description || tr('page_description') || ''

    return (
        <Helmet>
            <meta name="theme-color" content={accentColor} />
            
            <title>{title}</title>
            <meta name="description" content={description}/>
            <link rel="canonical" href={canonical} />
            
            <meta property="og:locale" content="en" />
            <meta property="og:locale:alternate" content="fr" />
            <meta property="og:locale:alternate" content="de" />
            <meta property="og:locale:alternate" content="ru" />
            <meta property="og:locale:alternate" content="es" />
            <meta property="og:locale:alternate" content="zh-CN" />
            <meta property="og:locale:alternate" content="zh-TW" />
            
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:site_name" content="Byzans" />
            <meta property="og:image" content={extra?.thumbnail || thumbnail} />
            {/* <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="613" /> */}
            
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={extra?.thumbnail ||thumbnail} />
            <meta name="twitter:creator" content="@byzansapp" />
            <meta name="twitter:site" content="@byzansapp" />
        </Helmet>
    )
}

// location and params to get route info
const useExtraSeo = (pageName: string, location: any, routeParams: any): SeoExtra | null => {
    try {
        const ctx = useContext(context)

        const seoKey = getSeoKey(pageName, location, routeParams)
        if (seoKey == null) {
            // We don't sent special seo from the server for this
            return null
        }

        const extraSeoObj = (ctx.extraSeo ?? {})[seoKey]

        return extraSeoObj
    } catch(e) {
        console.log('Error calculating SEO extra')
        console.log(e)
        return null
    }
}

const getSeoKey = (pageName: string, location: any, routeParams: any): string | null => {
    const query = new URLSearchParams(location.search)

    switch (pageName) {
        case 'share_profile':
            const { publicId } = routeParams
            
            return `${pageName}:${publicId}`
        case 'share_club':
        case 'share_chat':
            const clubId = query.get('clubId') || query.get("chat_id") // because we can get here either from /share or /private_chat

            return `${pageName}:${clubId}`
        case 'book':
            const isbn13 = query.get('isbn13')

            return `${pageName}:${isbn13}`
        default:
            return null
    }
}

const addHost = (host: string, path: string): string => {
    return new URL(path, host).toString()
}

export default Seo